import { ActivitySpinnerSize, ActivitySpinnerWidget, ButtonType, ButtonWidget } from '@tableau/widgets';
import React from 'react';
import { useResizeDetector } from 'react-resize-detector';

import { sendUsageEvent } from '~src/spacesPlatform/hooks/useUsage';

import { GuidedOnboardingBannerCardData, ServiceHooks,WelcomeBannerFeatureName } from '../services/GuidedOnboardingBannerService';
import { GuidedOnboardingStyles } from '../styles/GuidedOnboardingStyles';
import GuidedOnboardingBannerCard from './GuidedOnboardingBannerCard';
import { GuidedOnboardingBannerCloseButton } from './GuidedOnboardingBannerCloseButton';
import { GuidedOnboardingFilmStrip } from './GuidedOnboardingFilmStrip';
import { openGuidedOnboardingViewAllModal } from './GuidedOnboardingViewAllModal';
import { GuidedOnboardingWelcomeMessage } from './GuidedOnboardingWelcomeMessage';

// Note: the data type of all the following properties need to be string
// Because in the telemetry database when deserializing the JSON data it can only support the JSON data with the same data type of all properties.
export type WelcomeBannerTelemetryData = {
    completedActions: string;
    notCompletedActions: string;
    actionCount: string;
    completedActionCount: string;
};

export function GuidedOnboardingWelcomeBanner(): JSX.Element {
    // When the left navigation opens/closes, the resize detector is triggered multiple times during the transition.
    // This debounce with 10ms ensures resize is detected only once after the transition completes.
    // 10ms is the minimum time needed to detect the resize only once.
    const { width: containerWidth, ref: containerRef } = useResizeDetector<HTMLElement>({
        refreshMode: 'debounce',
        refreshRate: 10
    });
    const { hidden } = ServiceHooks.useBannerVisibility();
    const { data: cardData, isInitialLoading: isLoading } = ServiceHooks.useCardData();

    React.useEffect(() => {
        if (containerRef.current) {
            const containerElement = containerRef.current;
            const styleHeight = hidden ? '0px' : `${containerElement.scrollHeight}px`;
            containerElement.style.height = styleHeight;

            if (!hidden && cardData) {
                sendUsageEvent(WelcomeBannerFeatureName, 'impression', { a: getTelemetryDataString(cardData), c: containerElement.offsetWidth });
            }
        }
    }, [hidden, cardData, containerRef]);

    return (
        <div ref={containerRef} className={GuidedOnboardingStyles.welcomeBannerContainer}>
            {
                hidden || (isLoading && <LoadingSpinner />) ||
                    <section
                        className={GuidedOnboardingStyles.welcomeBanner}
                        data-tb-test-id={GuidedOnboardingTestIds.welcomeBanner}
                    >
                        <div className={GuidedOnboardingStyles.bannerMessageWrapper}>
                            <GuidedOnboardingWelcomeMessage bannerWidth={containerWidth} />
                            <ButtonWidget
                                buttonType={ButtonType.Outline}
                                className={GuidedOnboardingStyles.viewAllButtonBelow}
                                data-tb-test-id={GuidedOnboardingTestIds.viewAllButton}
                                type="button"
                                onClick={() => openGuidedOnboardingViewAllModal()}
                            >
                                <b>View all</b>
                            </ButtonWidget>
                        </div>
                        <GuidedOnboardingFilmStrip>
                            {cardData && cardData.map(action => (
                                <GuidedOnboardingBannerCard key={action.id} {...action} />
                            ))}
                        </GuidedOnboardingFilmStrip>

                        <GuidedOnboardingBannerCloseButton />
                    </section>
            }
        </div>
    );
}

function LoadingSpinner() {
    return (
        <div className={GuidedOnboardingStyles.onboardingSpinnerContainer}>
            <ActivitySpinnerWidget spinnerSize={ActivitySpinnerSize.Large} data-tb-test-id={GuidedOnboardingTestIds.onboardingSpinner} />
        </div>
    );
}

export enum GuidedOnboardingTestIds {
    welcomeBanner = 'guided-onboarding-welcome-banner',
    bannerMessage = 'guided-onboarding-banner-message',
    bannerGreeting = 'guided-onboarding-banner-greeting',
    bannerGreetingBody = 'guided-onboarding-banner-greeting-body',
    filmStrip = 'guided-onboarding-film-strip',
    filmStripContentBody = 'guided-onboarding-film-strip-content-body',
    filmStripPrevButton = 'guided-onboarding-film-strip-prev-button',
    filmStripNextButton = 'guided-onboarding-film-strip-next-button',
    closeButton = 'guided-onboarding-close-button',
    helpMenuPopover = 'guided-onboarding-help-menu-popover',
    bannerCard = 'guided-onboarding-banner-card',
    videoContent = 'guided-onboarding-video-content',
    cardTitle = 'guided-onboarding-card-title',
    cardDescription = 'guided-onboarding-card-description',
    cardType = 'guided-onboarding-card-type',
    cardLength = 'guided-onboarding-card-length',
    onboardingSpinner = 'guided-onboarding-spinner',
    onboardingCardCheckmark = 'guided-onboarding-card-checkmark',
    viewAllButton = 'guided-onboarding-view-all-button'
}

function getTelemetryDataString(cardData: GuidedOnboardingBannerCardData[]) {
    let completedActionCount = 0;

    const telemetryData: WelcomeBannerTelemetryData = {
        completedActions: '',
        notCompletedActions: '',
        actionCount: cardData.length.toString(),
        completedActionCount: ''
    };

    cardData.forEach(card => {
        if (card.isCompleted) {
            telemetryData.completedActions += ('<' + card.id + '>');
            completedActionCount++;
        } else {
            telemetryData.notCompletedActions += ('<' + card.id + '>');
        }
    });

    telemetryData.completedActionCount = completedActionCount.toString();

    return JSON.stringify(telemetryData);
}